import * as React from 'react';
import { FormattedDate, SimpleTD } from '../..';
import { ColGroup, SearchResultColumn, SearchResultsTable, TableBody, TableHead } from '../../search-results-table';
import { useQuery } from '@apollo/client';
import {
  SortDirection,
  FetchAuditNotesForEntityQuery,
  FetchAuditNotesForEntityQueryVariables,
} from '@css-xp/graphtypes';
import { CommerceNoteSearchParams, EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { fetchAuditNotesForEntity } from './queries/fetch-audit-notes';
import { memoize } from 'lodash';
import { useFeatureToggle } from '../../hooks/src/use-feature-toggle';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';
import { CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { Link } from 'react-router-dom';
export interface AuditTrailTabProps {
  id: string;
  entityName: string;
  entity?: string;
  adaUrl?: string;
}

type auditNoteMetaDatatype = 'supportTicketNumber' | 'internalNote' | 'financeRefundReason';

export const defaultAuditNotesSearchOptions: FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
  first: 15,
  sort: {
    sortFields: ['created', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

export const buildSerachParams = ({ id, entityName }: AuditTrailTabProps): CommerceNoteSearchParams | undefined => {
  const searchParams: CommerceNoteSearchParams = {};
  switch (entityName) {
    case EntityWithNotes.INVOICE:
      return { ...searchParams, invoiceKey: id };
    case EntityWithNotes.REFUND:
      return { ...searchParams, refundKey: id };
    case EntityWithNotes.ENTITLEMENT:
      return { ...searchParams, entitlementKey: id };
    case EntityWithNotes.QUOTE:
      return { ...searchParams, quoteKey: id };
    case EntityWithNotes.CONTACT:
      return { ...searchParams, contactKey: id };
    case EntityWithNotes.TransactionAccount:
      return { ...searchParams, transactionAccountNumber: id };
    default:
      return undefined;
  }
};

export const AuditNotes = function ({ id, entityName }: AuditTrailTabProps) {
  const searchParams = buildSerachParams({
    id,
    entityName,
  });
  const { data, error, loading, fetchMore } = useQuery<
    FetchAuditNotesForEntityQuery,
    FetchAuditNotesForEntityQueryVariables
  >(fetchAuditNotesForEntity, {
    variables: {
      searchParams,
      searchOptions: defaultAuditNotesSearchOptions,
    },
    displayName: 'fetchAuditNotesByEntityId',
    notifyOnNetworkStatusChange: true,
  });

  const [isEnabled] = useFeatureToggle();
  const handleLoadMoreClick = () => {
    const endCursor = queryResults?.pageInfo?.endCursor;
    fetchMore({
      query: fetchAuditNotesForEntity,
      variables: {
        searchParams: searchParams,
        searchOptions: {
          ...defaultAuditNotesSearchOptions,
          after: endCursor,
        },
      },
      //@ts-ignore
      updateQuery: (
        previousResult,
        {
          fetchMoreResult,
        }: {
          fetchMoreResult: FetchAuditNotesForEntityQuery;
        }
      ) => {
        if (fetchMoreResult?.auditNotes?.nodes && previousResult.auditNotes?.nodes) {
          fetchMoreResult.auditNotes.nodes = [...previousResult.auditNotes.nodes, ...fetchMoreResult.auditNotes?.nodes];
        }

        return fetchMoreResult;
      },
    });
  };
  const getColumns = memoize((): SearchResultColumn[] => {
    const columns = [
      {
        name: 'Date',
        customClassNames: 'col-30',
      },
      {
        name: 'Update',
        customClassNames: 'col-40',
      },
      {
        name: 'Details',
        customClassNames: 'col-15',
      },
      {
        name: 'Made by',
        customClassNames: 'col-15',
      },
      {
        name: '',
      },
    ];

    return columns;
  });
  const queryResults = data?.auditNotes;
  const auditTrailNodes = queryResults?.nodes;
  const totalCount = queryResults?.totalCount || 0;
  const shouldShowLoadMore = queryResults?.pageInfo?.hasNextPage || false;
  const hasNoResults = totalCount === 0;
  const resultSetSize = auditTrailNodes?.length;

  return (
    <div className="statuspage-tab" data-testid="statuspage-audit-trail">
      <SearchResultsTable
        id="audit-trail"
        isLoading={loading}
        error={error?.toString()}
        noResults={hasNoResults}
        heading={'Audit trail'}
        loadMore={{
          showLoadMore: shouldShowLoadMore,
          totalCount: totalCount,
          resultsCount: resultSetSize,
          onLoadMoreClick: handleLoadMoreClick,
        }}
        entityName="audit trail"
        colSpan={4}
      >
        <ColGroup columns={getColumns()} />
        <TableHead columns={getColumns()} />
        <TableBody>
          {auditTrailNodes &&
            auditTrailNodes.map((auditNote, index) => {
              const created = auditNote?.created;
              const body = auditNote?.body;
              const author = auditNote?.contactKey || auditNote?.author;
              const metadata = auditNote?.metadata as Array<{ fieldName: string; fieldValue: string }>;
              const metadataRender = metadata?.reduce((acc: Record<auditNoteMetaDatatype, string>, dataObject) => {
                if (dataObject && dataObject.fieldName) {
                  const key = dataObject.fieldName as auditNoteMetaDatatype;
                  acc[key] = dataObject?.fieldValue;
                }
                return acc;
              }, {} as Record<auditNoteMetaDatatype, string>);
              return (
                <tr key={index}>
                  <td>
                    <FormattedDate date={created} showTime={true} />
                  </td>
                  {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) ? (
                    <SimpleTD
                      text={body}
                      handNote={metadataRender?.internalNote}
                      supportTicketNumber={metadataRender?.supportTicketNumber}
                    />
                  ) : (
                    <SimpleTD text={body} />
                  )}
                  <SimpleTD text={metadataRender?.financeRefundReason} />
                  <td>
                    {/*// @ts-ignore */}
                    <Link to={`${CONTACT_PATH_2}/${encodeURIComponent(author)}`}>{author}</Link>
                  </td>
                </tr>
              );
            })}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
};
